interface KaraokeFinalCtaProps {
  lang: 'en' | 'ja';
}

export default function KaraokeFinalCta({ lang }: KaraokeFinalCtaProps) {
  const content = {
    en: {
      title: 'Ready to Sing Your Heart Out?',
      subtitle: 'Book your Tokyo Karaoke Night experience now and create unforgettable memories.',
      cta: 'Book via WhatsApp',
      features: [
        'Instant confirmation',
        'Friendly local guide',
        'No stress, just fun'
      ]
    },
    ja: {
      title: '思いっきり歌う準備はできましたか？',
      subtitle: 'Tokyo Karaoke Night体験を今すぐ予約して、忘れられない思い出を作りましょう。',
      cta: 'WhatsAppで予約',
      features: [
        '即座に確認',
        'フレンドリーな地元ガイド',
        'ストレスなし、ただ楽しむだけ'
      ]
    }
  };

  const t = content[lang];

  const handleWhatsAppClick = () => {
    const message = lang === 'en' 
      ? 'Hi! I\'d like to book Tokyo Karaoke Night experience.'
      : 'こんにちは！Tokyo Karaoke Nightの予約をしたいです。';
    window.open(`https://wa.me/818041800418?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/ad91aad1d97f70a87b929050b3070f9e.jpg"
          alt="Karaoke Night"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/90 via-pink-900/85 to-black/90"></div>
      </div>

      {/* Animated Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
          {t.title}
        </h2>
        <p className="text-xl text-gray-200 mb-12 leading-relaxed">
          {t.subtitle}
        </p>

        {/* Features */}
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          {t.features.map((feature, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full"
            >
              <i className="ri-checkbox-circle-fill text-pink-400 text-xl"></i>
              <span className="text-white font-medium">{feature}</span>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <button
          onClick={handleWhatsAppClick}
          className="group inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-xl font-bold rounded-full shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
        >
          <i className="ri-whatsapp-line text-3xl"></i>
          <span>{t.cta}</span>
          <i className="ri-arrow-right-line text-2xl group-hover:translate-x-1 transition-transform"></i>
        </button>

        {/* Decorative Music Notes */}
        <div className="mt-16 flex justify-center gap-8 text-white/30">
          <i className="ri-music-2-line text-4xl animate-bounce" style={{ animationDelay: '0s' }}></i>
          <i className="ri-music-2-line text-5xl animate-bounce" style={{ animationDelay: '0.2s' }}></i>
          <i className="ri-music-2-line text-4xl animate-bounce" style={{ animationDelay: '0.4s' }}></i>
        </div>
      </div>
    </section>
  );
}